import type { SearchParams, SearchResultV5 } from '@seek/chalice-types';

import type { DataLayer } from '../DataLayer';
import { filterBlanks } from '../utils/filterBlanks';
import { queryPart } from '../utils/hashToUserQueryId';

import { getSEOMarketingLinkType } from './getSEOMarketingLinkType';
import { mapLocationInput } from './mapLocationInput';
import { isNullified, nullify } from './nullify';

export interface Search {
  query: SearchParams;
  result: SearchResultV5;
  flightId: string;
  userQueryId: string;
  isRadialFilterShown?: boolean;
  isRadialFilterNudgeShown?: boolean;
  locationInput?: string;
  relatedSearchDisplayCount?: number;
  hasQueryParams?: boolean;
}

export const formatClassificationIds = (ids?: string) =>
  ids
    ?.toString()
    .split(',')
    .map((id) => Number(id.trim()))
    .sort((a, b) => a - b)
    .join(',');

export const mapSearch = ({
  dataLayer: { siteCountry },
  search,
}: {
  dataLayer: DataLayer;
  search: Search;
}): DataLayer => {
  const {
    query,
    result,
    userQueryId,
    isRadialFilterShown,
    flightId,
    isRadialFilterNudgeShown,
    relatedSearchDisplayCount,
  } = search || {};

  const {
    advertiserid,
    classification,
    keywords,
    subclassification,
    daterange,
    worktype,
    workarrangement,
    salarytype,
    salaryrange,
    distance,
    sortmode,
    graduatesearch,
    page = '1',
    companysearch,
    where,
  } = query || {};

  const { data = [], location, totalCount } = result ?? {};

  const { locationDescription, whereId } = location ?? {};

  const companySuggestion = result?.suggestions?.company;

  return filterBlanks({
    seekAdvertiserId: advertiserid,
    experimentFlightId: flightId,
    searchId: userQueryId,
    searchKeyword: nullify(keywords),
    classificationId: nullify(formatClassificationIds(classification)),
    subClassificationId: nullify(formatClassificationIds(subclassification)),
    locationInput: nullify(mapLocationInput({ where, country: siteCountry })),
    marketingSeoLinkType: nullify(getSEOMarketingLinkType(search)),
    relatedSearchDisplayCount: nullify(relatedSearchDisplayCount),
    searchLocation: locationDescription,
    searchDateRange: daterange,
    searchWorkTypeIds: nullify(worktype),
    searchRemoteOptions: nullify(workarrangement),
    ...mapSearchSalaryRange({ searchSalaryRange: salaryrange }),
    searchSalaryType: salarytype,
    searchRadialDistance: nullify(distance),
    sortedBy: sortmode !== 'ListedDate' ? 'KeywordRelevance' : 'ListedDate',
    searchGraduate: graduatesearch,
    pageNumber: page,
    searchLocationId: Number(whereId) || undefined,
    ...(companySuggestion
      ? { companySearchType: companysearch ? 'exact' : 'broad' }
      : {}),
    jobsCount: totalCount,
    firstThreeJobIds: data.slice(0, 3).map(({ id }) => parseInt(id, 10)),
    isRadialFilterShown,
    isRadialFilterNudgeShown,
  });
};

export const isRefinement = ({
  searchId,
  previousSearchId,
  previousPage,
}: DataLayer = {}): boolean =>
  previousPage === 'search results' &&
  Boolean(searchId) &&
  Boolean(previousSearchId) &&
  queryPart(previousSearchId) !== queryPart(searchId);

const radialFilterDistanceSelected = (
  searchRadialDistance: string | typeof undefined,
): string => {
  if (searchRadialDistance === undefined || isNullified(searchRadialDistance)) {
    return '';
  }

  if (searchRadialDistance === '0') {
    return 'exact';
  }

  return `${searchRadialDistance}km`;
};

export const evaluateRadialFilterValues = (
  dataLayer: DataLayer,
): {
  isRadialFilterShown: boolean;
  isRadialFilterNudgeShown: boolean;
  radialFilterDistanceSelected: string;
} => ({
  isRadialFilterShown: Boolean(dataLayer.isRadialFilterShown),
  isRadialFilterNudgeShown: Boolean(dataLayer.isRadialFilterNudgeShown),
  radialFilterDistanceSelected: radialFilterDistanceSelected(
    dataLayer.searchRadialDistance,
  ),
});

export const mapSearchSalaryRange = (dataLayer: DataLayer) => {
  const [searchSalaryRangeMin, searchSalaryRangeMax] =
    dataLayer.searchSalaryRange?.split('-') ?? ['', ''];

  return {
    searchSalaryRange: nullify(dataLayer.searchSalaryRange),
    searchSalaryRangeMax: nullify(searchSalaryRangeMax),
    searchSalaryRangeMin: nullify(searchSalaryRangeMin),
  };
};
